import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { UserPlus, Users } from 'lucide-react';

const steps = [
  {
    num: '01',
    title: 'Create your profile',
    description: 'Add a strong bio and professional picture for maximum engagement.',
    image: '/images/apply-screen-1.jpg',
  },
  {
    num: '02',
    title: 'Access the app',
    description: 'All future DI events will be posted here.',
    image: '/images/apply-screen-2.jpg',
  },
  {
    num: '03',
    title: 'Access your space',
    description: 'Space-specific Mentors, internship opportunities, and tailored workshops, all announced here.',
    image: '/images/apply-screen-3.jpg',
  },
  {
    num: '04',
    title: 'Book a virtual "coffee chat"',
    description: 'Message up to one available Mentor a week and have a 30-min chat on a topic of your choice.',
    image: '/images/apply-screen-4.jpg',
  },
  {
    num: '05',
    title: 'Resources bank',
    description: 'Access a bank of exclusive resources to help you with interviews, CVs, and much more.',
    image: '/images/apply-screen-5.jpg',
  },
];

const Apply = () => {
  const [activeStep, setActiveStep] = useState(0);
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-cream pt-28 sm:pt-32 md:pt-36 pb-12 sm:pb-16 md:pb-20 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <p className="text-dark-green/60 font-mono tracking-[0.25em] text-xs sm:text-sm uppercase mb-5">
            <span className="text-dark-green/40">&rarr;</span> Dharma Initiative &middot; Apply
          </p>
          <h1 className="font-serif text-4xl sm:text-5xl md:text-7xl lg:text-8xl leading-tight tracking-tight mb-8 max-w-6xl">
            <span className="text-gradient-hero">Apply today.</span>
            <br />
            <span className="text-gradient-hero-light italic">It's free.</span>
          </h1>
          <p className="text-dark-green/75 text-lg sm:text-xl md:text-2xl leading-snug max-w-3xl mb-8">
            Choose your path and become part of the Dharma Initiative community.
          </p>
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <a
              href="https://apps.apple.com/gb/app/dharma-initiative/id6757341684"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="apply-app-store-link"
              className="inline-flex items-center gap-3 bg-dark-green text-cream px-5 sm:px-6 py-3 sm:py-4 rounded-full hover:bg-dark-green/90 transition-colors duration-300 group"
            >
              <svg className="w-5 h-5" viewBox="0 0 384 512" fill="currentColor" aria-hidden>
                <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/>
              </svg>
              <div className="flex flex-col items-start leading-tight">
                <span className="text-[10px] uppercase tracking-widest font-mono text-cream/70">Download on the</span>
                <span className="text-base sm:text-lg font-medium">App Store</span>
              </div>
              <span aria-hidden className="ml-1 group-hover:translate-x-0.5 transition-transform">&rarr;</span>
            </a>
            <p className="text-dark-green/60 text-xs sm:text-sm font-mono tracking-tight max-w-xs">
              Login requires application approval.
            </p>
          </div>
        </div>
      </section>

      {/* Options Section */}
      <section className="bg-dark-green py-14 sm:py-18 md:py-22 px-4 sm:px-6 border-t border-dark-green">
        <div className="max-w-6xl mx-auto">
          <div className="mb-10 md:mb-14">
            <p className="text-cream/50 tracking-[0.25em] text-xs sm:text-sm uppercase mb-3">
              <span className="text-cream/30">&rarr;</span> 01 / Choose your path
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-cream leading-tight max-w-3xl">
              Become a Mentee or Mentor.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-0 md:gap-px bg-cream/15 border-y border-cream/15">
            {/* Become a Mentee */}
            <article className="group bg-dark-green flex flex-col">
              <div className="relative aspect-[4/5] sm:aspect-[5/4] overflow-hidden">
                <img
                  src="https://framerusercontent.com/images/DSVlTBPjtCuyuUSMF9n68LXtdFw.jpg?width=736&height=1308"
                  alt="Become a Mentee"
                  className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700"
                />
                <div className="absolute top-5 left-5 flex items-center gap-2 text-cream font-mono text-xs tracking-widest">
                  <span className="w-1.5 h-1.5 bg-cream rounded-full" />
                  /001 &middot; MENTEE
                </div>
              </div>
              <div className="flex flex-col flex-1 p-6 sm:p-8 md:p-10 gap-5">
                <h3 className="font-serif text-3xl sm:text-4xl md:text-5xl text-cream leading-[1.05]">
                  Become a Mentee.
                </h3>
                <p className="text-cream/85 text-base md:text-lg leading-relaxed">
                  Join our network for <span className="italic">free</span> and fulfil your true potential.
                </p>
                <p className="text-cream/55 text-xs sm:text-sm font-mono uppercase tracking-tight">
                  Generally for uni students &amp; professionals/entrepreneurs aged &lt;30.
                </p>
                <div className="mt-auto pt-4">
                  <Link
                    to="/apply-mentee"
                    data-testid="apply-as-mentee"
                    className="inline-flex items-center gap-2 bg-cream text-dark-green px-6 py-3 sm:px-7 sm:py-4 rounded-full text-base font-medium hover:bg-cream/90 transition-all duration-300 group/btn"
                  >
                    Apply as Mentee
                    <span aria-hidden className="group-hover/btn:translate-x-1 transition-transform">&rarr;</span>
                  </Link>
                </div>
              </div>
            </article>

            {/* Become a Mentor */}
            <article className="group bg-dark-green flex flex-col">
              <div className="relative aspect-[4/5] sm:aspect-[5/4] overflow-hidden">
                <img
                  src="https://framerusercontent.com/images/yuJPnL6Dx9iLUpN6S5wQHLlk.jpeg?width=1902&height=1600"
                  alt="Become a Mentor"
                  className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700"
                />
                <div className="absolute top-5 left-5 flex items-center gap-2 text-cream font-mono text-xs tracking-widest">
                  <span className="w-1.5 h-1.5 bg-cream rounded-full" />
                  /002 &middot; MENTOR
                </div>
              </div>
              <div className="flex flex-col flex-1 p-6 sm:p-8 md:p-10 gap-5">
                <h3 className="font-serif text-3xl sm:text-4xl md:text-5xl text-cream leading-[1.05]">
                  Become a Mentor.
                </h3>
                <p className="text-cream/85 text-base md:text-lg leading-relaxed">
                  Become a changemaker for the next generation.
                </p>
                <p className="text-cream/55 text-xs sm:text-sm font-mono uppercase tracking-tight">
                  Generally for experienced professionals/entrepreneurs aged &gt;30.
                </p>
                <div className="mt-auto pt-4">
                  <Link
                    to="/apply-mentor"
                    data-testid="apply-as-mentor"
                    className="inline-flex items-center gap-2 bg-cream text-dark-green px-6 py-3 sm:px-7 sm:py-4 rounded-full text-base font-medium hover:bg-cream/90 transition-all duration-300 group/btn"
                  >
                    Apply as Mentor
                    <span aria-hidden className="group-hover/btn:translate-x-1 transition-transform">&rarr;</span>
                  </Link>
                </div>
              </div>
            </article>
          </div>
        </div>
      </section>

      {/* Product Showcase - How it Works */}
      <section className="bg-cream py-16 sm:py-20 md:py-24 px-4 sm:px-6 border-t border-dark-green/10">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <div className="mb-12 md:mb-16">
            <p className="text-dark-green/60 tracking-[0.25em] text-xs sm:text-sm uppercase mb-3">
              <span className="text-dark-green/40">&rarr;</span> 03 / How it works
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-dark-green leading-tight max-w-3xl">
              See the app in five steps.
            </h2>
          </div>

          <div className="grid lg:grid-cols-12 gap-10 md:gap-14 items-start">
            {/* Product image */}
            <div className="lg:col-span-5 lg:sticky lg:top-28">
              <div className="relative mx-auto" style={{ maxWidth: '320px' }}>
                <div className="relative">
                  <div className="absolute -inset-6 bg-gradient-to-br from-dark-green/15 to-dark-green/[0.03] blur-3xl pointer-events-none" />
                  <div className="relative bg-dark-green rounded-[2rem] p-[3px]">
                    <div className="bg-black rounded-[1.85rem] overflow-hidden relative" style={{ aspectRatio: '540/951' }}>
                      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-4 bg-black rounded-b-2xl z-10" />
                      {steps.map((s, i) => (
                        <img
                          key={s.num}
                          src={s.image}
                          alt={s.title}
                          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${
                            i === activeStep ? 'opacity-100' : 'opacity-0'
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Step list */}
            <div className="lg:col-span-7">
              <ul className="divide-y divide-dark-green/15 border-t border-b border-dark-green/15">
                {steps.map((s, i) => {
                  const isActive = i === activeStep;
                  return (
                    <li key={s.num}>
                      <button
                        type="button"
                        onClick={() => setActiveStep(i)}
                        onMouseEnter={() => setActiveStep(i)}
                        data-testid={`apply-step-${s.num}`}
                        aria-pressed={isActive}
                        className={`w-full text-left py-6 md:py-8 flex gap-5 md:gap-8 items-start group transition-colors duration-300 ${
                          isActive ? 'opacity-100' : 'opacity-60 hover:opacity-100'
                        }`}
                      >
                        <span
                          className={`font-mono text-xs md:text-sm pt-1.5 tracking-widest transition-colors ${
                            isActive ? 'text-dark-green' : 'text-dark-green/40'
                          }`}
                        >
                          {s.num}
                        </span>
                        <div className="flex-1 min-w-0">
                          <h3
                            className={`font-serif text-2xl sm:text-3xl md:text-4xl leading-tight mb-2 transition-colors ${
                              isActive ? 'text-dark-green' : 'text-dark-green/70'
                            }`}
                          >
                            {s.title}
                          </h3>
                          <p
                            className={`text-sm md:text-base leading-relaxed transition-all duration-500 overflow-hidden text-dark-green/75 ${
                              isActive ? 'max-h-40 opacity-100 mt-1' : 'max-h-0 opacity-0'
                            }`}
                          >
                            {s.description}
                          </p>
                        </div>
                        <span
                          aria-hidden
                          className={`pt-2 text-dark-green transition-transform duration-300 ${
                            isActive ? 'translate-x-1 opacity-100' : 'opacity-0 -translate-x-1'
                          }`}
                        >
                          &rarr;
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Info Section */}
      <section className="bg-cream py-14 sm:py-18 md:py-22 px-4 sm:px-6 border-t border-dark-green/10">
        <div className="max-w-7xl mx-auto">
          <div className="mb-10 md:mb-14">
            <p className="text-dark-green/60 font-mono tracking-[0.25em] text-xs sm:text-sm uppercase mb-3">
              <span className="text-dark-green/40">&rarr;</span> 04 / Next steps
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-dark-green leading-tight max-w-3xl">
              What happens next.
            </h2>
          </div>

          <ul className="border-t border-dark-green/15">
            {[
              {
                num: '01',
                title: 'Submit your application',
                desc: 'Click the apply button above and fill out the application form.',
              },
              {
                num: '02',
                title: 'Review process',
                desc: 'Our team will review your application and reach out to you with a decision within 2 weeks.',
              },
              {
                num: '03',
                title: 'Get started',
                desc: "If approved, you'll gain access to our network and can begin your journey with the Dharma Initiative.",
              },
            ].map((step) => (
              <li
                key={step.num}
                className="group grid md:grid-cols-12 gap-4 sm:gap-8 items-baseline py-7 sm:py-9 md:py-10 border-b border-dark-green/15 hover:bg-dark-green/[0.02] transition-colors duration-300"
              >
                <span className="md:col-span-1 font-mono text-xs md:text-sm tracking-widest text-dark-green/50 pt-1">
                  {step.num}
                </span>
                <h3 className="md:col-span-4 font-serif text-2xl sm:text-3xl md:text-4xl text-dark-green leading-tight">
                  {step.title}
                </h3>
                <p className="md:col-span-7 text-dark-green/75 text-sm sm:text-base md:text-lg leading-relaxed">
                  {step.desc}
                </p>
              </li>
            ))}
          </ul>
        </div>
      </section>
    </div>
  );
};

export default Apply;
